import { fg } from '@confluence/feature-gating';
import { fetchQuery as relayFetchQuery } from 'react-relay';
import type { GraphQLResponse, GraphQLSingularResponse, OperationType } from 'relay-runtime';
import { getRequest, Observable } from 'relay-runtime';

import { startCaptureGraphQlErrors, stopCaptureGraphQlErrors } from '@atlassian/relay-errors';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { normalizeRelayError } from './normalizeRelayError.ts';

export type ErrorPolicy = 'none' | 'ignore' | 'all';

export type FetchQueryErrorPolicyOptions = {
	errorPolicy?: ErrorPolicy;
	/**
	 * Bypass the Relay store entirely: execute straight against the network layer and hand back the
	 * raw response tree, so `RelayResponseNormalizer` never runs and no record is written.
	 *
	 * Backs the `'no-store'` fetch policy. Callers must *also* suppress their own store reads and
	 * writes (initial snapshot, `retain`/`subscribe`, `commitPayload`) — this flag only covers the
	 * fetch. See `mapFetchPolicyForQuery.ts` for why the policy exists.
	 */
	noStore?: boolean;
};

type RelayFetchQueryArgs<TQuery extends OperationType> = Parameters<typeof relayFetchQuery<TQuery>>;

/**
 * Executes the operation through the Relay **network layer** rather than the environment, which is
 * what skips normalization. Going through `getNetwork()` instead of raw `fetch` deliberately keeps
 * everything the network layer provides: auth, `StatusCodeRetryMiddleware`, batching,
 * network-status middleware, tracing, GraphQL error capture (`captureErrors` runs in the batch
 * fetcher), and the SSR response replay in `readDataFromStream` — so a bypassed operation still
 * reuses SSR-streamed data instead of issuing a second request. Only the normalizer is skipped.
 */
const executeWithoutStore = <TQuery extends OperationType>(
	environment: RelayFetchQueryArgs<TQuery>[0],
	query: RelayFetchQueryArgs<TQuery>[1],
	variables: RelayFetchQueryArgs<TQuery>[2],
	options?: RelayFetchQueryArgs<TQuery>[3],
): Observable<TQuery['response']> => {
	const request = getRequest(query);

	return environment
		.getNetwork()
		.execute(request.params, variables ?? {}, options?.networkCacheConfig ?? {}, null)
		.map((response: GraphQLResponse) => {
			// `@defer`/`@stream` operations emit multiple payloads, and the subscriber below completes
			// on the first one, so incremental fragments would be dropped. None of the current
			// `'no-store'` callers use them; revisit here if that changes.
			const singular = (Array.isArray(response) ? response[0] : response) as
				| GraphQLSingularResponse
				| undefined;
			return singular?.data as TQuery['response'];
		});
};

export type FetchQueryWithErrorPolicyResult<TData> = {
	data: TData;
	error?: ApolloErrorInterop;
};

export const fetchQueryWithErrorPolicy = <TQuery extends OperationType>(
	environment: RelayFetchQueryArgs<TQuery>[0],
	query: RelayFetchQueryArgs<TQuery>[1],
	variables: RelayFetchQueryArgs<TQuery>[2],
	options?: RelayFetchQueryArgs<TQuery>[3],
	errorPolicyOptions?: FetchQueryErrorPolicyOptions,
): Observable<FetchQueryWithErrorPolicyResult<TQuery['response']>> => {
	const errorPolicy = errorPolicyOptions?.errorPolicy ?? 'none';
	const operationName = (query as { params: { name: string } }).params.name;
	const errorCollector = startCaptureGraphQlErrors();

	// The error-policy handling below is identical either way: `captureErrors` runs in the network
	// layer, so GraphQL errors are collected even when the normalizer is skipped.
	const source = errorPolicyOptions?.noStore
		? executeWithoutStore<TQuery>(environment, query, variables, options)
		: relayFetchQuery<TQuery>(environment, query, variables, options);

	return Observable.create((sink) => {
		source.subscribe({
			next: (data) => {
				const errors = stopCaptureGraphQlErrors(errorCollector);
				const graphQLErrors = errors
					.filter((error) => error.meta.operationName === operationName)
					.flatMap((e) => e.errors);

				if (graphQLErrors.length > 0) {
					const error = new ApolloErrorInterop({ graphQLErrors });

					if (errorPolicy === 'ignore') {
						sink.next({ data });
						sink.complete();
						return;
					}

					if (errorPolicy === 'all') {
						sink.next({ data, error });
						sink.complete();
						return;
					}

					sink.error(error);
					return;
				}

				sink.next({ data });
				sink.complete();
			},
			error: (error: Error) => {
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				sink.error(fg('confluence_relay_parity_fixes') ? normalizeRelayError(error) : error);
			},
			complete: () => {
				sink.complete();
			},
		});
	});
};

export const fetchQuery = <TQuery extends OperationType>(
	...args: [
		...Parameters<typeof relayFetchQuery<TQuery>>,
		errorPolicyOptions?: FetchQueryErrorPolicyOptions,
	]
): Observable<TQuery['response']> => {
	return Observable.create((sink) => {
		fetchQueryWithErrorPolicy<TQuery>(...args).subscribe({
			next: ({ data }) => {
				sink.next(data);
				sink.complete();
			},
			error: (error: Error) => {
				sink.error(error);
			},
			complete: () => {
				sink.complete();
			},
		});
	});
};
